/**
 * UI documents (`*.ui.json`, format `prism.ui/1`) for the UI editor: types,
 * parse / serialize, tree helpers and preview style helpers. Layout is not
 * done here: the preview asks the engine (`prism_wasm` `UiLayout`, see
 * ./engine-layout.ts) so it matches the game exactly.
 */

export const UI_DOC_EXT = '.ui.json';
export const UI_STYLE_EXT = '.uistyle.json';
export const UI_FORMAT = 'prism.ui/1';

export const ELEMENT_TYPES = [
  'panel',
  'container',
  'image',
  'text',
  'button',
  'toggle',
  'slider',
  'inputField',
  'scrollView',
  'dropdown',
  'line',
  'shape',
] as const;
export type ElementType = (typeof ELEMENT_TYPES)[number];

export const SELECTABLE: ReadonlySet<string> = new Set(['button', 'toggle', 'slider', 'inputField', 'dropdown']);

export const ALIGNMENTS = [
  'upperLeft',
  'upperCenter',
  'upperRight',
  'middleLeft',
  'middleCenter',
  'middleRight',
  'lowerLeft',
  'lowerCenter',
  'lowerRight',
] as const;

export type Vec2 = [number, number];

export interface RectTransform {
  anchorMin?: Vec2;
  anchorMax?: Vec2;
  pivot?: Vec2;
  anchoredPosition?: Vec2;
  sizeDelta?: Vec2;
  scale?: Vec2;
  /** Degrees around the pivot, counter-clockwise. */
  rotation?: number;
}

export interface LayoutGroup {
  type?: 'vertical' | 'horizontal' | 'grid';
  padding?: number | { left?: number; right?: number; top?: number; bottom?: number };
  spacing?: number | Vec2;
  childAlignment?: string;
  childControlWidth?: boolean;
  childControlHeight?: boolean;
  childForceExpandWidth?: boolean;
  childForceExpandHeight?: boolean;
  reverseArrangement?: boolean;
  cellSize?: Vec2;
  constraint?: 'flexible' | 'fixedColumnCount' | 'fixedRowCount';
  constraintCount?: number;
  startCorner?: string;
  startAxis?: string;
}

/** One element. Unknown keys are kept as they are. */
export interface UiNode {
  id?: string;
  type?: ElementType;
  class?: string;
  style?: Record<string, unknown>;
  rect?: RectTransform;
  visible?: boolean;
  interactable?: boolean;
  text?: string;
  placeholder?: string;
  image?: string;
  imageType?: 'simple' | 'sliced' | 'filled';
  fillAmount?: number;
  fillMethod?: 'horizontal' | 'vertical' | 'radial90' | 'radial180' | 'radial360';
  fillOrigin?: string;
  fillClockwise?: boolean;
  opacity?: number;
  points?: Vec2[];
  from?: string;
  to?: string;
  fromAnchor?: Vec2;
  toAnchor?: Vec2;
  /** `shape`: vector paths (commands like ["moveTo", x, y], ["arc", cx, cy, r, a0, a1], ["close"]). */
  paths?: ShapePath[];
  /** `shape`: path coordinates from the rect's top-left, y down. */
  yDown?: boolean;
  /** Tooltip text (rich text) and extra tooltip style classes. */
  tooltip?: string;
  tooltipClass?: string;
  drag?: { type?: string; ghost?: string; ghostOffset?: Vec2; ghostOpacity?: number; buttons?: number[] };
  drop?: { accepts?: string[] };
  worldAnchor?: {
    entity?: string;
    position?: [number, number, number];
    offset?: [number, number, number];
    fadeDistance?: Vec2;
    maxDistance?: number;
    scaleDistance?: Vec2;
    scale?: Vec2;
    occlude?: boolean;
    fadeTime?: number;
    hideOffscreen?: boolean;
  };
  on?: Record<string, string>;
  value?: number;
  minValue?: number;
  maxValue?: number;
  isOn?: boolean;
  options?: string[];
  layout?: LayoutGroup;
  template?: boolean;
  bind?: string;
  format?: string;
  onClick?: string;
  onValueChanged?: string;
  onSubmit?: string;
  children?: UiNode[];
  [key: string]: unknown;
}

/** One path of a `shape` element (Painter2D-like). */
export interface ShapePath {
  commands?: (string | number)[][];
  /** A color, or true = the style's fillColor. */
  fill?: unknown;
  /** A color, true = the style's lineColor, false = none (absent: stroked when not filled). */
  stroke?: unknown;
  lineWidth?: number;
  lineCap?: 'butt' | 'round' | 'square';
  lineJoin?: 'miter' | 'round' | 'bevel';
  fillRule?: 'nonZero' | 'evenOdd';
}

export interface CanvasScaler {
  mode?: 'constantPixelSize' | 'scaleWithScreenSize' | 'constantPhysicalSize';
  scaleFactor?: number;
  referenceResolution?: Vec2;
  screenMatchMode?: 'matchWidthOrHeight' | 'expand' | 'shrink';
  match?: number;
}

export interface UiDoc {
  format: string;
  name?: string;
  canvas?: {
    renderMode?: 'screenSpaceOverlay' | 'screenSpaceCamera' | 'worldSpace';
    sortOrder?: number;
    scaler?: CanvasScaler;
    size?: Vec2;
    pixelsPerUnit?: number;
    billboard?: boolean;
    [key: string]: unknown;
  };
  styles?: string[];
  elements: UiNode[];
  [key: string]: unknown;
}

export function parseUiDoc(text: string): { doc: UiDoc | null; error: string | null } {
  // A new, empty file starts as an empty document.
  if (!text.trim()) return { doc: newUiDoc(''), error: null };
  try {
    const v = JSON.parse(text) as unknown;
    if (!v || typeof v !== 'object' || Array.isArray(v)) return { doc: null, error: 'The document must be a JSON object.' };
    const doc = v as UiDoc;
    if (!Array.isArray(doc.elements)) doc.elements = [];
    return { doc, error: null };
  } catch (e) {
    return { doc: null, error: e instanceof Error ? e.message : String(e) };
  }
}

export function serializeUiDoc(doc: UiDoc): string {
  return `${JSON.stringify(doc, null, 2)}\n`;
}

export function newUiDoc(name: string): UiDoc {
  return {
    format: UI_FORMAT,
    name,
    canvas: { scaler: { mode: 'scaleWithScreenSize', referenceResolution: [1920, 1080], match: 0.5 } },
    elements: [],
  };
}

/** Path of an element: child indices from the top level. */
export type NodePath = number[];

export const pathKey = (p: NodePath) => p.join('.');

export function nodeAt(doc: UiDoc, path: NodePath): UiNode | null {
  let list = doc.elements;
  let node: UiNode | null = null;
  for (const i of path) {
    node = list[i] ?? null;
    if (!node) return null;
    list = node.children ?? [];
  }
  return node;
}

/** The array holding the element at `path` and its index there. */
export function siblingsOf(doc: UiDoc, path: NodePath): { list: UiNode[]; index: number } | null {
  if (path.length === 0) return null;
  const parent = path.length === 1 ? null : nodeAt(doc, path.slice(0, -1));
  if (path.length > 1 && !parent) return null;
  const list = parent ? (parent.children ??= []) : doc.elements;
  return { list, index: path[path.length - 1]! };
}

export interface FlatEntry {
  node: UiNode;
  path: NodePath;
  depth: number;
}

export function flatten(doc: UiDoc): FlatEntry[] {
  const out: FlatEntry[] = [];
  const walk = (nodes: UiNode[], prefix: NodePath) => {
    nodes.forEach((n, i) => {
      const path = [...prefix, i];
      out.push({ node: n, path, depth: prefix.length });
      walk(n.children ?? [], path);
    });
  };
  walk(doc.elements, []);
  return out;
}

export function allIds(doc: UiDoc): Set<string> {
  return new Set(flatten(doc).map((f) => f.node.id).filter((id): id is string => !!id));
}

export function uniqueId(doc: UiDoc, base: string): string {
  const ids = allIds(doc);
  if (!ids.has(base)) return base;
  for (let i = 2; ; i++) if (!ids.has(`${base}_${i}`)) return `${base}_${i}`;
}

/** A new element with sensible starting values for its type. */
export function newElement(doc: UiDoc, type: ElementType): UiNode {
  const id = uniqueId(doc, type === 'inputField' ? 'input' : type === 'scrollView' ? 'scroll' : type);
  const base: UiNode = { id, type, rect: { sizeDelta: [160, 40] } };
  switch (type) {
    case 'panel':
    case 'container':
      return { ...base, rect: { sizeDelta: [300, 200] } };
    case 'text':
      return { ...base, text: 'Text' };
    case 'button':
      return { ...base, text: 'Button' };
    case 'toggle':
      return { ...base, text: 'Toggle' };
    case 'slider':
      return { ...base, rect: { sizeDelta: [200, 20] }, value: 0.5 };
    case 'inputField':
      return { ...base, rect: { sizeDelta: [240, 40] }, placeholder: 'Enter text…' };
    case 'dropdown':
      return { ...base, options: ['Option A', 'Option B'], value: 0 };
    case 'scrollView':
      return { ...base, rect: { sizeDelta: [300, 240] }, layout: { type: 'vertical', spacing: 4, childControlWidth: true, childForceExpandWidth: true } };
    case 'image':
      return { ...base, rect: { sizeDelta: [64, 64] } };
    case 'line':
      return { ...base, rect: { anchorMin: [0, 0], anchorMax: [1, 1], sizeDelta: [0, 0] }, points: [[0, 0], [100, 100]] };
    case 'shape':
      return {
        ...base,
        rect: { sizeDelta: [48, 48] },
        yDown: true,
        paths: [{ commands: [['arc', 24, 24, 20, 0, 360], ['close']], fill: true }],
      };
  }
}

/** Anchor presets (Unity's anchor preset grid). */
export const ANCHOR_PRESETS: { label: string; min: Vec2; max: Vec2 }[] = [
  { label: '↖', min: [0, 1], max: [0, 1] },
  { label: '↑', min: [0.5, 1], max: [0.5, 1] },
  { label: '↗', min: [1, 1], max: [1, 1] },
  { label: '←', min: [0, 0.5], max: [0, 0.5] },
  { label: '•', min: [0.5, 0.5], max: [0.5, 0.5] },
  { label: '→', min: [1, 0.5], max: [1, 0.5] },
  { label: '↙', min: [0, 0], max: [0, 0] },
  { label: '↓', min: [0.5, 0], max: [0.5, 0] },
  { label: '↘', min: [1, 0], max: [1, 0] },
  { label: '⇔ top', min: [0, 1], max: [1, 1] },
  { label: '⇔ mid', min: [0, 0.5], max: [1, 0.5] },
  { label: '⇔ bottom', min: [0, 0], max: [1, 0] },
  { label: '⇕ left', min: [0, 0], max: [0, 1] },
  { label: '⇕ center', min: [0.5, 0], max: [0.5, 1] },
  { label: '⇕ right', min: [1, 0], max: [1, 1] },
  { label: 'stretch', min: [0, 0], max: [1, 1] },
];

// ---------------------------------------------------------------------------
// Rect transforms (editing) and preview helpers
// ---------------------------------------------------------------------------

/** An element rect in canvas units, y up (from the engine layout). */
export interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
  /** Visual transform (rotation / style transforms) `[a, b, c, d, e, f]`, canvas units, y up. */
  xform?: [number, number, number, number, number, number];
}

/** Every rect field with its default (`rotation` only when set, so documents stay clean). */
export type FullRect = Required<Omit<RectTransform, 'rotation'>> & Pick<RectTransform, 'rotation'>;

export function rectDefaults(r: RectTransform | undefined): FullRect {
  return {
    ...(r?.rotation ? { rotation: r.rotation } : {}),
    anchorMin: r?.anchorMin ?? [0.5, 0.5],
    anchorMax: r?.anchorMax ?? [0.5, 0.5],
    pivot: r?.pivot ?? [0.5, 0.5],
    anchoredPosition: r?.anchoredPosition ?? [0, 0],
    sizeDelta: r?.sizeDelta ?? [100, 100],
    scale: r?.scale ?? [1, 1],
  };
}

function pad(p: LayoutGroup['padding']) {
  if (typeof p === 'number') return { left: p, right: p, top: p, bottom: p };
  return { left: p?.left ?? 0, right: p?.right ?? 0, top: p?.top ?? 0, bottom: p?.bottom ?? 0 };
}

const ALIGN_FACTORS: Record<string, Vec2> = {
  upperLeft: [0, 0],
  upperCenter: [0.5, 0],
  upperRight: [1, 0],
  middleLeft: [0, 0.5],
  middleCenter: [0.5, 0.5],
  middleRight: [1, 0.5],
  lowerLeft: [0, 1],
  lowerCenter: [0.5, 1],
  lowerRight: [1, 1],
};

export const alignFactors = (a: string | undefined): Vec2 => ALIGN_FACTORS[a ?? 'upperLeft'] ?? [0, 0];

// ---------------------------------------------------------------------------
// Style sheets (preview only)
// ---------------------------------------------------------------------------

export type StyleProps = Record<string, unknown>;
export interface StyleSheet {
  /** Alias → font file, or a family by weight (`{ "medium": "...", "bold": "..." }`). */
  fonts?: Record<string, string | Record<string, string>>;
  classes?: Record<string, StyleProps>;
}

/** Same neutral defaults as the engine's built-in sheet. */
export const BUILTIN_SHEET: StyleSheet = {
  classes: {
    '*': { textSize: 16, textColor: [1, 1, 1, 1], textAlign: 'upperLeft' },
    panel: { backgroundColor: [0.3, 0.3, 0.3, 0.7], padding: 8 },
    image: { imageColor: [1, 1, 1, 1] },
    button: { backgroundColor: [0.4, 0.4, 0.4, 1], textAlign: 'middleCenter' },
    toggle: { backgroundColor: [0.4, 0.4, 0.4, 1], fillColor: [1, 1, 1, 1], textAlign: 'middleLeft' },
    slider: { backgroundColor: [0.25, 0.25, 0.25, 1], fillColor: [1, 1, 1, 1], handleColor: [1, 1, 1, 1], handleSize: 16 },
    inputField: { backgroundColor: [0.25, 0.25, 0.25, 1], padding: 6, textAlign: 'middleLeft', placeholderColor: [1, 1, 1, 0.5] },
    dropdown: { backgroundColor: [0.4, 0.4, 0.4, 1], padding: 6, textAlign: 'middleLeft' },
    scrollView: { scrollbarColor: [1, 1, 1, 0.3], scrollbarWidth: 6 },
    line: { lineColor: [1, 1, 1, 1], lineWidth: 2 },
  },
};

const STATE_KEYS = new Set(['hover', 'focused', 'pressed', 'checked', 'disabled', 'dragging', 'dropAccept', 'dropReject']);

/** Base props for an element (states ignored) from ordered sheets + inline style. */
export function resolveStyle(sheets: StyleSheet[], n: UiNode): StyleProps {
  const classes = [n.type ?? 'panel', ...(n.class ?? '').split(/\s+/).filter(Boolean)];
  const out: StyleProps = {};
  const apply = (c: StyleProps | undefined) => {
    if (!c) return;
    for (const [k, v] of Object.entries(c)) {
      if (STATE_KEYS.has(k)) continue;
      // Per-side borders merge side by side (like CSS border-top-color over border-color).
      out[k] = k === 'borderColor' || k === 'borderWidth' ? mergeSides(out[k], v) : v;
    }
  };
  for (const s of sheets) {
    apply(s.classes?.['*']);
    for (const c of classes) apply(s.classes?.[c]);
  }
  apply(n.style);
  if (n.type === 'toggle' && n.isOn) {
    for (const s of sheets) for (const c of classes) apply(s.classes?.[c]?.checked as StyleProps | undefined);
  }
  if (n.interactable === false) {
    for (const s of sheets) for (const c of classes) apply(s.classes?.[c]?.disabled as StyleProps | undefined);
  }
  return out;
}

/** Every class name defined by the sheets. */
export function sheetClasses(sheets: StyleSheet[]): string[] {
  const set = new Set<string>();
  for (const s of sheets) for (const k of Object.keys(s.classes ?? {})) if (k !== '*' && !ELEMENT_TYPES.includes(k as ElementType)) set.add(k);
  return [...set].sort();
}

/** `[r,g,b,a]` / `"#rrggbb(aa)"` → CSS color. */
export function cssColor(c: unknown, fallback = 'transparent'): string {
  if (Array.isArray(c) && c.length >= 3) {
    const [r, g, b, a] = c as number[];
    return `rgba(${Math.round((r ?? 0) * 255)}, ${Math.round((g ?? 0) * 255)}, ${Math.round((b ?? 0) * 255)}, ${a ?? 1})`;
  }
  if (typeof c === 'string' && /^#[0-9a-f]{6}([0-9a-f]{2})?$/i.test(c)) {
    if (c.length === 9) return `rgba(${parseInt(c.slice(1, 3), 16)}, ${parseInt(c.slice(3, 5), 16)}, ${parseInt(c.slice(5, 7), 16)}, ${parseInt(c.slice(7, 9), 16) / 255})`;
    return c;
  }
  return fallback;
}

export function paddingOf(p: unknown): { left: number; right: number; top: number; bottom: number } {
  return pad(p as LayoutGroup['padding']);
}

export const SIDES = ['top', 'right', 'bottom', 'left'] as const;

/** `[top, right, bottom, left]` of a per-side style value: one value, or `{ top, right, bottom, left }`. */
export function sidesOf<T>(v: unknown, fallback: T): [T, T, T, T] {
  if (v && typeof v === 'object' && !Array.isArray(v)) {
    const o = v as Record<string, T | undefined>;
    return SIDES.map((s) => o[s] ?? fallback) as [T, T, T, T];
  }
  const one = (v ?? fallback) as T;
  return [one, one, one, one];
}

/** A per-side value from four sides: one value when all match, else an object. */
export function sidesValue<T>(sides: readonly T[]): T | Record<string, T> {
  const first = sides[0] as T;
  const same = sides.every((s) => JSON.stringify(s) === JSON.stringify(first));
  return same ? first : Object.fromEntries(SIDES.map((s, i) => [s, sides[i] as T]));
}

/** Sides set in `next` replace those of `prev` (a single value sets all four). */
function mergeSides(prev: unknown, next: unknown): unknown {
  if (!next || typeof next !== 'object' || Array.isArray(next)) return next;
  const base: Record<string, unknown> = prev && typeof prev === 'object' && !Array.isArray(prev)
    ? { ...(prev as Record<string, unknown>) }
    : prev === undefined ? {} : Object.fromEntries(SIDES.map((s) => [s, prev]));
  return { ...base, ...(next as Record<string, unknown>) };
}

/** SVG path data (CSS pixels) of a shape path; `toPx` maps path units to the preview. */
export function shapeSvgPath(cmds: (string | number)[][], toPx: (p: [number, number]) => [number, number]): string {
  const out: string[] = [];
  let pen: [number, number] | null = null;
  let start: [number, number] = [0, 0];
  const pt = (p: [number, number]) => toPx(p).map((v) => Math.round(v * 100) / 100).join(' ');
  const to = (p: [number, number]) => {
    out.push(`${pen ? 'L' : 'M'} ${pt(p)}`);
    if (!pen) start = p;
    pen = p;
  };
  for (const c of cmds) {
    const name = String(c[0] ?? '');
    const raw = c.slice(1).map((x) => Number(x) || 0);
    const n = [0, 1, 2, 3, 4, 5].map((i) => raw[i] ?? 0) as [number, number, number, number, number, number];
    if (name === 'moveTo') {
      out.push(`M ${pt([n[0], n[1]])}`);
      pen = [n[0], n[1]];
      start = pen;
    } else if (name === 'lineTo') to([n[0], n[1]]);
    else if (name === 'arc' || name === 'arcTo') {
      // Flattened (the engine flattens to a quarter pixel).
      let pts: [number, number][] = [];
      if (name === 'arc') {
        const [cx, cy, r, a0, a1] = n;
        const steps = Math.max(8, Math.ceil(Math.abs(a1 - a0) / 6));
        for (let k = 0; k <= steps; k++) {
          const a = ((a0 + ((a1 - a0) * k) / steps) * Math.PI) / 180;
          pts.push([cx + r * Math.cos(a), cy + r * Math.sin(a)]);
        }
      } else {
        const p0: [number, number] = pen ?? [n[0], n[1]];
        const [x1, y1, x2, y2, r] = n;
        const u0: [number, number] = [p0[0] - x1, p0[1] - y1];
        const u1: [number, number] = [x2 - x1, y2 - y1];
        const l0 = Math.hypot(u0[0], u0[1]);
        const l1 = Math.hypot(u1[0], u1[1]);
        const cos = l0 && l1 ? (u0[0] * u1[0] + u0[1] * u1[1]) / (l0 * l1) : 1;
        const half = Math.acos(Math.max(-1, Math.min(1, cos))) / 2;
        if (!l0 || !l1 || r <= 0 || Math.abs(Math.tan(half)) < 1e-6) pts = [[x1, y1]];
        else {
          const t = r / Math.tan(half);
          const a: [number, number] = [x1 + (u0[0] / l0) * t, y1 + (u0[1] / l0) * t];
          const b: [number, number] = [x1 + (u1[0] / l1) * t, y1 + (u1[1] / l1) * t];
          const bis: [number, number] = [u0[0] / l0 + u1[0] / l1, u0[1] / l0 + u1[1] / l1];
          const bl = Math.hypot(bis[0], bis[1]) || 1;
          const d = r / Math.sin(half);
          const c0: [number, number] = [x1 + (bis[0] / bl) * d, y1 + (bis[1] / bl) * d];
          const s0 = Math.atan2(a[1] - c0[1], a[0] - c0[0]);
          let s1 = Math.atan2(b[1] - c0[1], b[0] - c0[0]);
          while (s1 - s0 > Math.PI) s1 -= 2 * Math.PI;
          while (s0 - s1 > Math.PI) s1 += 2 * Math.PI;
          for (let k = 0; k <= 12; k++) {
            const ang = s0 + ((s1 - s0) * k) / 12;
            pts.push([c0[0] + r * Math.cos(ang), c0[1] + r * Math.sin(ang)]);
          }
        }
      }
      for (const p of pts) to(p);
    } else if (name === 'bezierTo') {
      if (!pen) out.push(`M ${pt([n[0], n[1]])}`);
      out.push(`C ${pt([n[0], n[1]])} ${pt([n[2], n[3]])} ${pt([n[4], n[5]])}`);
      pen = [n[4], n[5]];
    } else if (name === 'quadTo') {
      if (!pen) out.push(`M ${pt([n[0], n[1]])}`);
      out.push(`Q ${pt([n[0], n[1]])} ${pt([n[2], n[3]])}`);
      pen = [n[2], n[3]];
    } else if (name === 'close') {
      out.push('Z');
      pen = start;
    }
  }
  return out.join(' ');
}

// ---------------------------------------------------------------------------
// Text and events (preview / property helpers; the engine does the real work)
// ---------------------------------------------------------------------------

export const FONT_WEIGHTS: Record<string, number> = {
  thin: 100,
  extraLight: 200,
  light: 300,
  regular: 400,
  normal: 400,
  medium: 500,
  semiBold: 600,
  bold: 700,
  extraBold: 800,
  black: 900,
};

/** `"bold"` / `700` / `"700"` → 700; null for unknown values. */
export function parseWeight(v: unknown): number | null {
  if (typeof v === 'number') return v >= 1 && v <= 1000 ? v : null;
  if (typeof v !== 'string') return null;
  if (/^\d+$/.test(v.trim())) return parseWeight(Number(v));
  const k = Object.keys(FONT_WEIGHTS).find((n) => n.toLowerCase() === v.trim().toLowerCase());
  return k ? FONT_WEIGHTS[k]! : null;
}

/** UI events (on_ui_event and `on` handlers). */
export const UI_EVENTS = [
  'click',
  'rightClick',
  'middleClick',
  'doubleClick',
  'pointerEnter',
  'pointerExit',
  'pointerDown',
  'pointerUp',
  'scroll',
  'beginDrag',
  'drag',
  'endDrag',
  'dragCancel',
  'dragEnter',
  'dragExit',
  'drop',
  'valueChanged',
  'submit',
  'select',
  'deselect',
  'cancel',
] as const;

export interface RichSpan {
  text: string;
  color?: string;
  bold: boolean;
  italic: boolean;
  /** Size in canvas units (absent = the element's). */
  size?: number;
}

/** Rich text tags (`<b>`, `<i>`, `<color=…>`, `<size=…>`, `<br>`) as spans, like the engine; unknown tags stay text. */
export function richSpans(text: string, baseSize: number): RichSpan[] {
  const out: RichSpan[] = [];
  const colors: string[] = [];
  const sizes: number[] = [];
  let bold = 0;
  let italic = 0;
  let cur = '';
  const style = () => ({ color: colors.at(-1), bold: bold > 0, italic: italic > 0, size: sizes.at(-1) });
  let curStyle = style();
  const flush = () => {
    if (cur) out.push({ text: cur, ...curStyle });
    cur = '';
  };
  let i = 0;
  while (i < text.length) {
    const lt = text.indexOf('<', i);
    if (lt < 0) {
      cur += text.slice(i);
      break;
    }
    cur += text.slice(i, lt);
    const gt = text.indexOf('>', lt);
    if (gt < 0) {
      cur += text.slice(lt);
      break;
    }
    const tag = text.slice(lt + 1, gt).trim();
    const low = tag.toLowerCase();
    let known = true;
    let br = false;
    if (low === 'b') bold++;
    else if (low === '/b') bold = Math.max(0, bold - 1);
    else if (low === 'i') italic++;
    else if (low === '/i') italic = Math.max(0, italic - 1);
    else if (low === '/color') colors.pop();
    else if (low === '/size') sizes.pop();
    else if (low === 'br' || low === 'br/') br = true;
    else if (low.startsWith('color=')) colors.push(tag.slice(6).replace(/["']/g, ''));
    else if (low.startsWith('size=')) {
      const v = low.slice(5).replace(/"/g, '');
      const base = sizes.at(-1) ?? baseSize;
      const n = v.endsWith('%') ? (baseSize * Number(v.slice(0, -1))) / 100 : /^[+-]/.test(v) ? base + Number(v) : Number(v);
      if (Number.isFinite(n) && n > 0) sizes.push(n);
      else known = false;
    } else known = false;
    if (!known) cur += text.slice(lt, gt + 1);
    else if (br) cur += '\n';
    else {
      const next = style();
      if (JSON.stringify(next) !== JSON.stringify(curStyle)) {
        flush();
        curStyle = next;
      }
    }
    i = gt + 1;
  }
  flush();
  return out;
}
